import { useEffect, useState } from "react"
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

type TImageCarouselProps = {
  images: string[]
  label: string
  className?: string
}

/**
 * Minimal image carousel for the product feed's repeatable image sets.
 *
 * Built from primitives rather than a library: the repo has no carousel
 * component and no swiper/embla dependency, and the need here is narrow —
 * step through a handful of product images with a thumbnail strip.
 */
export default function ImageCarousel({
  images,
  label,
  className,
}: TImageCarouselProps) {
  const [index, setIndex] = useState(0)
  const [broken, setBroken] = useState<Record<number, boolean>>({})

  // Selecting a different product reuses this component instance, so reset the
  // position when the set changes rather than pointing past the new end.
  useEffect(() => {
    setIndex(0)
    setBroken({})
  }, [images])

  if (images.length === 0) return null

  const safeIndex = Math.min(index, images.length - 1)
  const hasMultiple = images.length > 1

  const step = (delta: number) =>
    setIndex(current => {
      const next = (current + delta + images.length) % images.length
      return next
    })

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-white">{label}</span>
        <span className="text-xs text-gray-400">
          {safeIndex + 1} / {images.length}
        </span>
      </div>

      <div className="relative group rounded-system-md overflow-hidden bg-basic-black border border-basic-blue">
        {broken[safeIndex] ? (
          <div className="flex h-64 items-center justify-center text-xs text-gray-500">
            Image failed to load
          </div>
        ) : (
          <img
            src={images[safeIndex]}
            alt={`${label} ${safeIndex + 1}`}
            loading="lazy"
            className="h-64 w-full object-contain"
            onError={() =>
              setBroken(current => ({ ...current, [safeIndex]: true }))
            }
          />
        )}

        {hasMultiple && (
          <>
            <button
              type="button"
              aria-label="Previous image"
              onClick={() => step(-1)}
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Next image"
              onClick={() => step(1)}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      {hasMultiple && (
        <div className="flex gap-2 overflow-x-auto scrollbar pb-1">
          {images.map((image, thumbIndex) => (
            <button
              key={`${image}-${thumbIndex}`}
              type="button"
              onClick={() => setIndex(thumbIndex)}
              className={cn(
                "h-12 w-12 flex-shrink-0 overflow-hidden rounded border",
                thumbIndex === safeIndex
                  ? "border-brand"
                  : "border-basic-blue opacity-60 hover:opacity-100",
              )}
            >
              <img
                src={image}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
